import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Upload, Star, ArrowLeft } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminReviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const navigate = useNavigate();

  const [newReview, setNewReview] = useState({
    name: '',
    rating: 5,
    comment: '',
    date: 'January 2025',
    avatar: '',
    avatarImage: '',
    platform: 'Google'
  });

  const [editReview, setEditReview] = useState({
    name: '',
    rating: 5,
    comment: '',
    date: '',
    avatar: '',
    avatarImage: '',
    platform: 'Google'
  });

  const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    return { Authorization: `Bearer ${token}` };
  };

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchReviews();
  }, [navigate]);

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/reviews`, {
        headers: getAuthHeaders()
      });
      setReviews(response.data.reviews || []);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('adminToken');
        navigate('/admin');
      }
      toast.error('Failed to load reviews');
    } finally {
      setLoading(false);
    }
  };

  const handleAddReview = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/reviews`,
        newReview,
        { headers: getAuthHeaders() }
      );
      toast.success('Review added successfully!');
      setIsAddDialogOpen(false);
      setNewReview({
        name: '',
        rating: 5,
        comment: '',
        date: 'January 2025',
        avatar: '',
        avatarImage: '',
        platform: 'Google'
      });
      fetchReviews();
    } catch (error) {
      toast.error('Failed to add review');
    }
  };

  const handleEditReview = async (e) => {
    e.preventDefault();
    if (!selectedReview) return;

    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/reviews/${selectedReview.id}`,
        editReview,
        { headers: getAuthHeaders() }
      );
      toast.success('Review updated successfully!');
      setIsEditDialogOpen(false);
      setSelectedReview(null);
      fetchReviews();
    } catch (error) {
      toast.error('Failed to update review');
    }
  };

  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm('Are you sure you want to delete this review?')) {
      return;
    }

    try {
      await axios.delete(
        `${BACKEND_URL}/api/admin/reviews/${reviewId}`,
        { headers: getAuthHeaders() }
      );
      toast.success('Review deleted successfully!');
      fetchReviews();
    } catch (error) {
      toast.error('Failed to delete review');
    }
  };

  const handleAvatarUpload = async (reviewId, file) => {
    setUploadingAvatar(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/reviews/${reviewId}/avatar`,
        formData,
        {
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      toast.success('Avatar uploaded successfully!');
      fetchReviews();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload avatar');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const openEditDialog = (review) => {
    setSelectedReview(review);
    setEditReview({
      name: review.name,
      rating: review.rating,
      comment: review.comment,
      date: review.date,
      avatar: review.avatar,
      avatarImage: review.avatarImage || '',
      platform: review.platform
    });
    setIsEditDialogOpen(true);
  };

  const renderStars = (rating) => {
    return (
      <div className="flex space-x-1">
        {[...Array(5)].map((_, index) => (
          <Star 
            key={index}
            size={16}
            className={index < rating ? 'fill-amber-500 text-amber-500' : 'text-gray-300'}
          />
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading reviews...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <Button
                onClick={() => navigate('/admin/dashboard')}
                variant="outline"
                size="sm"
                data-testid="back-to-dashboard-btn"
              >
                <ArrowLeft size={18} className="mr-2" />
                Back to Dashboard
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-amber-900">Manage Reviews</h1>
                <p className="text-gray-600 text-sm">Add, edit, or delete customer reviews</p>
              </div>
            </div>
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              className="border-amber-600 text-amber-700 hover:bg-amber-50"
            >
              View Website
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Add Review Button */}
        <div className="mb-8">
          <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="add-new-review-btn">
                <Plus size={20} className="mr-2" />
                Add New Review
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Add New Review</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddReview} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="add-name">Reviewer Name *</Label>
                    <Input
                      id="add-name"
                      value={newReview.name}
                      onChange={(e) => setNewReview({ ...newReview, name: e.target.value })}
                      placeholder="e.g., Amina K."
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="add-avatar">Avatar Initials *</Label>
                    <Input
                      id="add-avatar"
                      value={newReview.avatar}
                      onChange={(e) => setNewReview({ ...newReview, avatar: e.target.value.toUpperCase() })}
                      placeholder="e.g., AK"
                      maxLength={3}
                      required
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="add-rating">Rating *</Label>
                    <Select 
                      value={newReview.rating.toString()} 
                      onValueChange={(val) => setNewReview({ ...newReview, rating: parseInt(val) })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="5">5 Stars</SelectItem>
                        <SelectItem value="4">4 Stars</SelectItem>
                        <SelectItem value="3">3 Stars</SelectItem>
                        <SelectItem value="2">2 Stars</SelectItem>
                        <SelectItem value="1">1 Star</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="add-platform">Platform *</Label>
                    <Select 
                      value={newReview.platform} 
                      onValueChange={(val) => setNewReview({ ...newReview, platform: val })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Google">Google</SelectItem>
                        <SelectItem value="Airbnb">Airbnb</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="add-date">Date *</Label>
                  <Input
                    id="add-date"
                    value={newReview.date}
                    onChange={(e) => setNewReview({ ...newReview, date: e.target.value })}
                    placeholder="e.g., January 2025"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="add-avatarImage">Profile Picture URL (Optional)</Label>
                  <Input
                    id="add-avatarImage"
                    value={newReview.avatarImage}
                    onChange={(e) => setNewReview({ ...newReview, avatarImage: e.target.value })}
                    placeholder="https://example.com/image.jpg"
                  />
                  <p className="text-xs text-gray-500 mt-1">Or upload after creating the review</p>
                </div>

                <div>
                  <Label htmlFor="add-comment">Review Comment *</Label>
                  <Textarea
                    id="add-comment"
                    value={newReview.comment}
                    onChange={(e) => setNewReview({ ...newReview, comment: e.target.value })}
                    placeholder="Write the review comment here..."
                    rows={4}
                    required
                  />
                </div>

                <Button type="submit" className="w-full bg-amber-600 hover:bg-amber-700">
                  Add Review
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Reviews List */}
        {reviews.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow">
            <p className="text-gray-600 mb-4">No reviews yet. Add your first review!</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Avatar</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rating</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Platform</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Comment</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {reviews.map((review) => (
                    <tr key={review.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="relative group">
                          {review.avatarImage ? (
                            <img 
                              src={review.avatarImage}
                              alt={review.name}
                              className="w-10 h-10 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-10 h-10 bg-amber-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                              {review.avatar}
                            </div>
                          )}
                          <label className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 cursor-pointer transition-opacity">
                            <Upload size={16} className="text-white" />
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => {
                                if (e.target.files[0]) {
                                  handleAvatarUpload(review.id, e.target.files[0]);
                                }
                              }}
                            />
                          </label>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{review.name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {renderStars(review.rating)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          review.platform === 'Google' 
                            ? 'bg-blue-100 text-blue-800' 
                            : 'bg-pink-100 text-pink-800'
                        }`}>
                          {review.platform}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {review.date}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 max-w-md truncate">
                          {review.comment}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <Button
                            onClick={() => openEditDialog(review)}
                            size="sm"
                            variant="outline"
                            className="text-blue-600 hover:text-blue-700"
                            data-testid={`edit-review-${review.id}`}
                          >
                            <Edit size={16} />
                          </Button>
                          <Button
                            onClick={() => handleDeleteReview(review.id)}
                            size="sm"
                            variant="outline"
                            className="text-red-600 hover:text-red-700"
                            data-testid={`delete-review-${review.id}`}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Review</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditReview} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-name">Reviewer Name</Label>
                <Input
                  id="edit-name"
                  value={editReview.name}
                  onChange={(e) => setEditReview({ ...editReview, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="edit-avatar">Avatar Initials</Label>
                <Input
                  id="edit-avatar"
                  value={editReview.avatar}
                  onChange={(e) => setEditReview({ ...editReview, avatar: e.target.value.toUpperCase() })}
                  maxLength={3}
                  required
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-rating">Rating</Label>
                <Select 
                  value={editReview.rating.toString()} 
                  onValueChange={(val) => setEditReview({ ...editReview, rating: parseInt(val) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">5 Stars</SelectItem>
                    <SelectItem value="4">4 Stars</SelectItem>
                    <SelectItem value="3">3 Stars</SelectItem>
                    <SelectItem value="2">2 Stars</SelectItem>
                    <SelectItem value="1">1 Star</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="edit-platform">Platform</Label>
                <Select 
                  value={editReview.platform} 
                  onValueChange={(val) => setEditReview({ ...editReview, platform: val })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Google">Google</SelectItem>
                    <SelectItem value="Airbnb">Airbnb</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="edit-date">Date</Label>
              <Input
                id="edit-date"
                value={editReview.date}
                onChange={(e) => setEditReview({ ...editReview, date: e.target.value })}
                required
              />
            </div>

            <div>
              <Label htmlFor="edit-avatarImage">Profile Picture URL</Label>
              <Input
                id="edit-avatarImage"
                value={editReview.avatarImage}
                onChange={(e) => setEditReview({ ...editReview, avatarImage: e.target.value })}
                placeholder="https://example.com/image.jpg"
              />
            </div>

            <div>
              <Label htmlFor="edit-comment">Review Comment</Label>
              <Textarea
                id="edit-comment"
                value={editReview.comment}
                onChange={(e) => setEditReview({ ...editReview, comment: e.target.value })}
                rows={4}
                required
              />
            </div>

            <Button type="submit" className="w-full bg-amber-600 hover:bg-amber-700">
              Update Review
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminReviews;
